// motion.js — gemeinsame Bewegung der App (Spec docs/superpowers/specs/2026-10-21-motion-design.md).
// Web Animations direkt am Element statt View Transitions: unterbrechbar, Eingaben gehen sofort durch.
// Regeln: nur transform/translate/scale/opacity/clip-path; eine neue Animation am selben Element startet beim
// Zwischenstand der alten; reduzierte Bewegung = nur Überblenden (120 ms linear), ohne Deckkraft keine Animation.
import springs from '../tokens/springs.json' with { type: 'json' };
import { spring, drag } from './physics.js';

const ALLOWED = new Set(['transform', 'translate', 'scale', 'opacity', 'clipPath', 'offset', 'easing', 'composite']);
const VISUAL = ['transform', 'translate', 'scale', 'opacity', 'clipPath'];
const running = new WeakMap();

/** Dauer + Kurve einer Expressive-Feder ('spatial-fast' … 'effects-slow'). */
export function timing(name) {
  const s = springs[name];
  if (!s) throw new Error('unbekannte Feder: ' + name);
  return { duration: s.durationMs, easing: s.easing };
}

const isReduced = () => typeof document !== 'undefined' && (document.documentElement.dataset.motion === 'reduced'
  || (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches));

/**
 * Animation abspielen. o: { spring = 'spatial-default', delay = 0, fill = 'none', reduced, getStyle }
 * Gibt die Animation zurück (oder null, wenn bei reduzierter Bewegung nichts zu tun ist).
 */
export function play(el, keyframes, o = {}) {
  for (const f of keyframes) for (const k of Object.keys(f)) if (!ALLOWED.has(k)) throw new Error('nicht animierbar (nur transform/opacity/clip-path): ' + k);
  if (!el || typeof el.animate !== 'function') return null;
  const reduced = o.reduced ?? isReduced();
  const getStyle = o.getStyle || ((e) => getComputedStyle(e));
  let kf = keyframes.map((f) => ({ ...f }));
  // Unterbrechen: laufende Animation abbrechen und beim aktuellen Stand weitermachen
  const prev = running.get(el);
  if (prev && prev.playState !== 'finished' && typeof prev.cancel === 'function') {
    const now = getStyle(el), first = {};
    for (const k of VISUAL) if (k in kf[0] && now[k] != null && now[k] !== '') first[k] = now[k];
    prev.cancel();
    kf[0] = { ...kf[0], ...first };
  }
  let opts;
  if (reduced) {
    if (!kf.some((f) => 'opacity' in f)) { running.delete(el); return null; }
    kf = [{ opacity: kf[0].opacity ?? 1 }, { opacity: kf[kf.length - 1].opacity ?? 1 }];
    opts = { duration: 120, easing: 'linear', fill: o.fill || 'none', delay: 0 };
  } else {
    opts = { ...timing(o.spring || 'spatial-default'), delay: o.delay || 0, fill: o.fill || 'none' };
  }
  const a = el.animate(kf, opts);
  running.set(el, a);
  return a;
}

const px = (n) => Math.round(n) + 'px';
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
/** Ausschnitt (clip-path inset) des Ziels, der genau das Rechteck des Ursprungs zeigt. */
export function insetOf(from, to, radius = 0) {
  const W = to.width, H = to.height;
  const l = clamp(from.left - to.left, 0, W), t = clamp(from.top - to.top, 0, H);
  const r = clamp(to.left + W - (from.left + from.width), 0, W), b = clamp(to.top + H - (from.top + from.height), 0, H);
  return `inset(${px(t)} ${px(r)} ${px(b)} ${px(l)} round ${px(radius)})`;
}

/**
 * Container-Transform (Liste/Marker → Detail), auf der GPU: eine Fläche wächst per transform aus dem Rechteck des
 * Ursprungs auf das Ziel (transform-origin 0 0), der Inhalt blendet darüber ein; reverse = zurück in den Ursprung.
 * Bewusst kein clip-path — Chrome animiert ihn auf dem Hauptthread, und das Detail zeichnet genau während des
 * Übergangs seinen Inhalt (gemessen: Bewegung stand bis zu 130 ms still).
 */
/** Verzögerung (ms), bis der Inhalt des Ziels einblendet: dann hat die Fläche ~80 % ihres Wegs. */
export const CT_CONTENT_DELAY = 150;
export function containerReveal(from, to, { reverse = false } = {}) {
  const sx = Math.max(0.01, from.width / (to.width || 1)), sy = Math.max(0.01, from.height / (to.height || 1));
  const r3 = (n) => Math.round(n * 1000) / 1000;
  const small = `translate(${px(from.left - to.left)}, ${px(from.top - to.top)}) scale(${r3(sx)}, ${r3(sy)})`;
  // Zurück blendet die Fläche beim Schrumpfen aus: die Zeile darunter erscheint fließend statt am Ende hervorzuspringen
  const surface = reverse ? [{ transform: 'none', opacity: 1 }, { transform: small, opacity: 0 }] : [{ transform: small }, { transform: 'none' }];
  // Inhalt: eigene Überblendung (Effekt-Feder) mit Verzögerung CT_CONTENT_DELAY — ein Zwischenschlüssel unter der
  // Federkurve der Fläche griffe zu früh (die Kurve ist nach 15 % der Zeit schon bei 43 % des Wegs) und legte den
  // vollflächigen Hintergrund des Ziels als Schleier über die noch kleine Fläche
  const content = reverse ? [{ opacity: 1 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 1 }];
  return { surface, content };
}

/** Fade Through (Navigationsziele ohne Beziehung): Eingang aus 92 %. */
export const fadeThrough = () => [{ opacity: 0, transform: 'scale(0.92)' }, { opacity: 1, transform: 'scale(1)' }];

/** Shared Axis: 'x' (Reiter, Richtung ±1) oder 'z' (Eltern → Kind = +1, zurück = −1). */
export function sharedAxis(axis, dir = 1) {
  if (axis === 'z') return [{ opacity: 0, transform: `scale(${dir > 0 ? 0.8 : 1.1})` }, { opacity: 1, transform: 'scale(1)' }];
  return [{ opacity: 0, transform: `translateX(${30 * Math.sign(dir || 1)}px)` }, { opacity: 1, transform: 'translateX(0px)' }];
}

/** Feder-Pop (mit spatial-fast überschwingt die Kurve selbst). */
export const pop = () => [{ transform: 'scale(0.6)' }, { transform: 'scale(1)' }];

/** Shape-Morph zwischen zwei Formen gleicher Punktzahl (Shape-Library --vc-shape-*). */
export const shapeMorph = (from, to) => [{ clipPath: from }, { clipPath: to }];

const depth = (p) => String(p || '').split('/').filter(Boolean).length;
/** Welches Muster ein Seitenwechsel bekommt; null = keine Bewegung. */
export function navMotion(prev, next) {
  if (!prev || !next) return null;
  if ((prev.nav || '') !== (next.nav || '')) return { kind: 'through' };
  if (prev.path === next.path) return null;
  const d = depth(next.path) - depth(prev.path);
  return d === 0 ? null : { kind: 'axis', axis: 'z', dir: d > 0 ? 1 : -1 };
}

/** Merken: das Symbol federt (spatial-fast); beim Merken läuft ein Ring nach außen (nur Deko, bei reduzierter Bewegung aus). */
export function heartBeat(icon, on, o = {}) {
  if (!icon) return null;
  const a = play(icon, on ? pop() : [{ transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { spring: 'spatial-fast', ...o });
  const reduced = o.reduced ?? isReduced();
  const host = icon.parentElement;
  if (on && !reduced && host && typeof document !== 'undefined') {
    const ring = document.createElement('span');
    ring.className = 'vc-burst';
    ring.setAttribute('aria-hidden', 'true');
    ring.style.left = (icon.offsetLeft + icon.offsetWidth / 2) + 'px';
    ring.style.top = (icon.offsetTop + icon.offsetHeight / 2) + 'px';
    host.appendChild(ring);
    const r = play(ring, [{ transform: 'translate(-50%, -50%) scale(0.4)', opacity: 0.6 }, { transform: 'translate(-50%, -50%) scale(2)', opacity: 0 }], { spring: 'effects-slow', reduced: false });
    const drop = () => ring.remove();
    if (r) r.finished.then(drop, drop); else drop();
  }
  return a;
}

/** Neu freigeschaltet: Abzeichen morpht vom Keks zur Sonne (Shape-Library), federt auf, gestaffelt (höchstens 8 × 90 ms). */
export function unlockReveal(icons, o = {}) {
  if (!icons.length || typeof document === 'undefined') return [];
  const root = getComputedStyle(document.documentElement);
  const from = root.getPropertyValue('--vc-shape-cookie4').trim(), to = root.getPropertyValue('--vc-shape-sunny').trim();
  if (!from || !to) return [];
  return icons.map((el, i) => play(el, [{ clipPath: from, transform: 'scale(0.4) rotate(-90deg)', opacity: 0 },
    { clipPath: to, transform: 'none', opacity: 1 }], { spring: 'spatial-slow', delay: Math.min(i, 8) * 90, fill: 'backwards', ...o }));
}

/** Feste Elemente in Seiten (schwebende Knöpfe usw.): ein transform am Vorfahren würde sie mitnehmen. */
export const FIXED_SEL = '.pk-fab, .ck-fab, .tr-fab, .vc-fab, .vc-toolbar.is-floating, [data-motion-fixed]';
const MAX_PARTS = 12;

/** Teile eines Bereichs, die sich bewegen dürfen, ohne ein festes Element mitzunehmen. */
export function movable(root) {
  if (!root.querySelector(FIXED_SEL)) return [root];
  const out = [];
  for (const c of root.children) if (!c.matches(FIXED_SEL)) out.push(...movable(c));
  return out;
}

/** Muster auf einen ganzen Bereich: Deckkraft auf den Bereich, Bewegung nur auf Teile ohne feste Elemente. */
export function playScene(root, keyframes, o = {}) {
  if (!root) return [];
  const parts = movable(root);
  if (parts.length === 1 && parts[0] === root) return [play(root, keyframes, o)];
  const op = keyframes.map((f) => ('opacity' in f ? { opacity: f.opacity } : {}));
  const tf = keyframes.map(({ opacity, ...rest }) => rest);
  const out = [play(root, op, o)];
  if (parts.length <= MAX_PARTS) for (const p of parts) out.push(play(p, tf, o));
  return out;
}

// ── Runde 2 (v442): Fade, Wischen, Zahlenrolle, Auf-/Zuklappen, FLIP ──
/** M3 „Fade“ für Elemente, die an Ort und Stelle erscheinen; nutzt die scale-Eigenschaft (ein vorhandenes transform bleibt). */
export const fadeIn = (from = 0.8) => [{ opacity: 0, scale: String(from) }, { opacity: 1, scale: '1' }];

/** Wisch-Entscheidung: dx (px, Vorzeichen = Richtung), v (px/ms), w = Breite. */
export function swipeOutcome(dx, v, w) {
  const dir = dx < 0 ? -1 : 1;
  const fast = Math.abs(v) > 0.5 && Math.sign(v) === dir;
  return { dismiss: Math.abs(dx) > w / 3 || fast, dir };
}

/** Neuer Wert: kommt aus der Richtung der Änderung herein (1 = größer → von unten). */
export const rollIn = (dir = 1) => [{ opacity: 0, transform: `translateY(${dir < 0 ? '-' : ''}0.6em)` }, { opacity: 1, transform: 'translateY(0)' }];

/** Aufklappen per Ausschnitt von oben (reverse = zuklappen) — keine Höhen-Animation. */
export function expand(reverse = false) {
  const shut = { opacity: 0, clipPath: 'inset(0px 0px 100% 0px)' }, open = { opacity: 1, clipPath: 'inset(0px 0px 0% 0px)' };
  return reverse ? [open, shut] : [shut, open];
}

/** FLIP: ein Element ist um dy verschoben worden → von der alten Lage aus hineinfedern. */
export const flip = (dy) => (Math.round(dy) ? [{ transform: `translateY(${Math.round(dy)}px)` }, { transform: 'translateY(0px)' }] : null);

/**
 * Wegwischen per Zeiger (waagerecht): das Element folgt dem Finger; losgelassen fliegt es mit seinem Schwung in
 * Wischrichtung hinaus (onDismiss) oder federt zurück. Senkrecht bleibt Scrollen. Gibt eine Aufräumfunktion zurück.
 */
export function swipeToDismiss(el, onDismiss) {
  const w = () => el.offsetWidth || 1;
  const show = (x) => {
    el.style.translate = Math.abs(x) > 0.5 ? x + 'px 0' : '';
    el.style.opacity = Math.abs(x) > 0.5 ? String(Math.max(0.2, 1 - Math.abs(x) / w())) : '';
  };
  const x = spring(0, { spring: 'spatial-fast', precision: 0.5, onUpdate: show });
  return drag(el, {
    axis: 'x', slop: 8,
    filter: (e) => !e.target.closest('button, a'),
    value: () => x.value,
    onStart: () => x.stop(),
    onMove: (v) => x.jump(v),
    onRelease: ({ value, velocity }) => {
      const out = swipeOutcome(value, velocity / 1000, w());
      if (!out.dismiss) { x.to(0, { velocity }); return; }
      if (isReduced()) { onDismiss(); return; }
      // Hinaus mit dem Wurf: Effekt-Feder (schwingt nicht zurück ins Bild), mindestens Richtungsschwung
      const fly = spring(value, { spring: 'effects-default', precision: 2, onUpdate: show });
      fly.to(out.dir * (w() + 40), { velocity: Math.sign(velocity) === out.dir ? velocity : out.dir * 600 }).then(onDismiss);
    },
  });
}
